import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, catchError, from, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { CloudLogsService, LogType } from './cloud-logs.service';
import { ProcessLog } from 'src/app/user-identification/models/user-identification';

@Injectable({
	providedIn: 'root',
})
export class CameraService {
	private streamSubject = new BehaviorSubject<MediaStream | null>(null);
	private allCamerasSubject = new BehaviorSubject<MediaDeviceInfo[]>([]);
	private changeCameraSubject = new Subject<string | undefined>();
	private mediaRecorder: MediaRecorder;
	private recordedChunks: Blob[] = [];
	private cloudLogsService = inject(CloudLogsService);
	private isChangingCamera = false;
	private lastAutomaticChangeTime = 0;
	private isProcessingOCR = false;
	private ocrProcessingTimeout: any = null;
	private hasAudio: boolean = false;
	/** Descarta respuestas tardías de comprobaciones de permiso solapadas (varios componentes / clics). */
	private permissionCheckSeq = 0;
	private lastPermissionResolveFrom = '';
	private lastPermissionResolveErrorName = '';
	private lastPermissionResolveErrorMessage = '';

	/**
	 * Videoinputs conmutables: solo `videoinput` con deviceId no vacío, sin duplicados.
	 * No se usa filterIosCameras aquí (labels dependen del idioma/Safari y generaban falsos positivos/negativos).
	 */
	public buildSwitchableCameraList(mediaDevices: MediaDeviceInfo[]): MediaDeviceInfo[] {
		const list = mediaDevices.filter((device: MediaDeviceInfo) => device.kind === 'videoinput');
		const withId = list.filter((d: MediaDeviceInfo) => Boolean(d.deviceId?.trim()));
		const seen = new Set<string>();
		return withId.filter((d: MediaDeviceInfo) => {
			if (seen.has(d.deviceId)) return false;
			seen.add(d.deviceId);
			return true;
		});
	}

	public filterIosCameras(mediaDevices: MediaDeviceInfo[]): MediaDeviceInfo[] {
		let camerasAlt: MediaDeviceInfo[] = [];
		const labelsIosFrontal = ['Cámara frontal', 'Front Camera'];
		const labelsIosTrasera3 = ['Cámara trasera triple', 'Back Triple Camera'];
		const labelsIosTrasera2 = ['Cámara trasera dual', 'Back Dual Camera'];
		const labelsIosTrasera1 = ['Cámara trasera', 'Back Camera', 'trasera', 'back'];
		const discard = ['teleobjetivo', 'telephoto', 'angular', 'wide'];
		camerasAlt = mediaDevices
			.filter((device: MediaDeviceInfo) => device.kind === 'videoinput')
			.filter(
				(d: MediaDeviceInfo) =>
					!d.label || !discard.some((word: string) => d.label.toLowerCase().includes(word.toLowerCase())),
			);

		const foundFrontCamera = camerasAlt.find((md: MediaDeviceInfo) => labelsIosFrontal.includes(md.label));
		const foundBackCamera = camerasAlt.find((md: MediaDeviceInfo) => labelsIosTrasera1.includes(md.label));
		const foundBackCameraOp2 = camerasAlt.find((md: MediaDeviceInfo) => labelsIosTrasera2.includes(md.label));
		const foundBackCameraOp3 = camerasAlt.find((md: MediaDeviceInfo) => labelsIosTrasera3.includes(md.label));
		if (foundFrontCamera && (foundBackCamera || foundBackCameraOp2 || foundBackCameraOp3)) {
			return [foundFrontCamera, foundBackCamera || foundBackCameraOp2 || foundBackCameraOp3];
		}
		return camerasAlt;
	}

	public getAllCameras(): void {
		navigator.mediaDevices
			.enumerateDevices()
			.then((mediaDevices: MediaDeviceInfo[]) => {
				const cameras: MediaDeviceInfo[] = this.buildSwitchableCameraList(mediaDevices);
				this.allCamerasSubject.next(cameras);

				// Cambiamos de cámara si no encontramos el id - pero con protecciones
				if (this.streamSubject.value && !this.isChangingCamera && !this.isProcessingOCR) {
					const currentDeviceId = this.getCurrentDeviceId();
					const foundCamera = cameras.find((md: MediaDeviceInfo) => md.deviceId === currentDeviceId);
					if (!foundCamera) {
						this.cloudLogsService.sendCloudLogs(
							{
								message: 'Cámara actual no encontrada en el listado de cámaras disponibles',
								currentDeviceId,
								cameras,
							},
							ProcessLog.PRELOAD,
							LogType.Warning,
						);
					}
					// Prevenir cambios automáticos duplicados en corto tiempo (< 2 segundos)
					const now = Date.now();
					const timeSinceLastChange = now - this.lastAutomaticChangeTime;

					if (!foundCamera && cameras?.length && timeSinceLastChange > 2000) {
						this.lastAutomaticChangeTime = now;
						this.isChangingCamera = true;
						this.changeCamera();
						// Reset flag after a delay to allow the change to complete
						setTimeout(() => {
							this.isChangingCamera = false;
						}, 5000);
					}
				}
			})
			.catch((err: any) => {
				this.cloudLogsService.sendCloudLogs({ allCameras: err?.toString() }, ProcessLog.PRELOAD, LogType.Error);
				this.allCamerasSubject.next([]);
			});
	}

	public hasGetUserMedia(): boolean {
		return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
	}

	public getCurrentDeviceId(): string | null {
		const stream = this.streamSubject.value;
		if (!stream) return '';
		const deviceId = stream.getVideoTracks()[0]?.getSettings()?.deviceId;
		return deviceId ?? null;
	}

	public getCurrentDeviceLabel(): string {
		const stream = this.streamSubject.value;
		if (!stream) return '';
		const videoTrack = stream.getVideoTracks()[0];
		return videoTrack && videoTrack.label ? videoTrack.label : null;
	}

	/** Libera tracks de un stream de prueba para no bloquear un segundo getUserMedia en VideoComponent. */
	private stopMediaStreamTracks(stream: MediaStream | null | undefined): void {
		if (!stream?.getTracks) {
			return;
		}
		stream.getTracks().forEach((track: MediaStreamTrack) => {
			try {
				track.stop();
			} catch {
				// El track puede estar ya detenido
			}
		});
	}

	/** Inicia una comprobación de permisos; usar con `isPermissionCheckCurrent(seq)` en el callback. */
	public beginPermissionCheck(): number {
		return ++this.permissionCheckSeq;
	}

	public isPermissionCheckCurrent(seq: number): boolean {
		return seq === this.permissionCheckSeq;
	}

	public getPermissionResolveContext(): {
		from: string;
		errorName?: string;
		errorMessage?: string;
	} | null {
		if (!this.lastPermissionResolveFrom) {
			return null;
		}
		return {
			from: this.lastPermissionResolveFrom,
			errorName: this.lastPermissionResolveErrorName || undefined,
			errorMessage: this.lastPermissionResolveErrorMessage || undefined,
		};
	}

	private setPermissionResolveContext(from: string, error?: { name?: string; message?: string }): void {
		this.lastPermissionResolveFrom = from;
		this.lastPermissionResolveErrorName = error?.name ?? '';
		this.lastPermissionResolveErrorMessage = error?.message ?? '';
	}

	/**
	 * Comprueba permisos de cámara sin probe `getUserMedia` si Permissions API ya reporta `granted`
	 * (evita carrera con `app-video`). Con `prompt` o sin API, usa probe (puede abrir prompt nativo).
	 */
	public resolveHasCameraPermission(constraints: MediaStreamConstraints): Observable<boolean> {
		if (!this.hasGetUserMedia()) {
			this.setPermissionResolveContext('no_getUserMedia');
			return of(false);
		}
		if (!navigator.permissions?.query) {
			this.setPermissionResolveContext('getUserMedia_probe');
			return this.checkCameraPermissions(constraints);
		}
		return from(
			navigator.permissions
				.query({ name: 'camera' as PermissionName })
				.then((status: PermissionStatus) => {
					if (status.state === 'denied') {
						this.setPermissionResolveContext('permissions_api_denied');
						return false;
					}
					if (status.state === 'granted') {
						this.setPermissionResolveContext('permissions_api_granted');
						return true;
					}
					this.setPermissionResolveContext('getUserMedia_probe');
					return null;
				})
				.catch(() => {
					this.setPermissionResolveContext('getUserMedia_probe');
					return null;
				}),
		).pipe(
			switchMap((apiResult: boolean | null) => {
				if (apiResult === false) {
					return of(false);
				}
				if (apiResult === true) {
					return of(true);
				}
				return this.checkCameraPermissions(constraints);
			}),
		);
	}

	public checkCameraPermissions(constraints: MediaStreamConstraints): Observable<boolean> {
		if (!this.hasGetUserMedia()) {
			this.setPermissionResolveContext('no_getUserMedia');
			return of(false);
		}
		return from(navigator.mediaDevices.getUserMedia(constraints)).pipe(
			tap((stream: MediaStream) => this.stopMediaStreamTracks(stream)),
			map(() => true),
			catchError((error) => {
				this.setPermissionResolveContext('getUserMedia_probe', {
					name: error?.name,
					message: error?.message,
				});
				this.cloudLogsService.sendCloudLogs(
					{ checkCameraPermissions: `Error: ${error?.toString()}`, constraints: constraints },
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
				return of(false);
			}),
		);
	}

	/**
	 * Elige un mimeType soportado por MediaRecorder (p. ej. Safari suele preferir mp4; Chrome webm).
	 */
	public getPreferredRecorderMimeType(): string | undefined {
		const candidates = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
		for (const mime of candidates) {
			if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) {
				return mime;
			}
		}
		return undefined;
	}

	public startRecording(stream: MediaStream | null): void {
		if (!stream || !stream.active) {
			this.cloudLogsService.sendCloudLogs(
				{ startRecording: `No hay stream` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			return;
		}
		try {
			this.recordedChunks = [];
			const mimeType = this.getPreferredRecorderMimeType();
			this.mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

			this.mediaRecorder.ondataavailable = (event) => {
				if (event.data.size > 0) this.recordedChunks.push(event.data);
			};

			this.mediaRecorder.start();
		} catch (err) {
			this.cloudLogsService.sendCloudLogs(
				{ startRecording: `Error: ${err?.toString()}` },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
		}
	}

	public stopRecording(): Promise<Blob> {
		return new Promise<Blob>((resolve, reject) => {
			if (!this.mediaRecorder) {
				resolve(new Blob(this.recordedChunks, { type: 'video/webm' }));
				return;
			}
			if (this.mediaRecorder.state === 'inactive') {
				const blobType = this.mediaRecorder.mimeType || 'video/webm';
				resolve(new Blob(this.recordedChunks, { type: blobType }));
				return;
			}
			this.mediaRecorder.onstop = () => {
				const blobType = this.mediaRecorder?.mimeType || 'video/webm';
				const blob = new Blob(this.recordedChunks, { type: blobType });
				resolve(blob);
			};
			try {
				this.mediaRecorder.stop();
			} catch (e) {
				reject(e);
				return;
			}
		});
	}

	get stream$(): Observable<MediaStream | null> {
		return this.streamSubject.asObservable();
	}

	get stream(): MediaStream | null {
		return this.streamSubject.value;
	}

	set stream(stream: MediaStream | null) {
		this.streamSubject.next(stream);
	}

	get changeCamera$(): Observable<string | undefined> {
		return this.changeCameraSubject.asObservable();
	}

	public changeCamera(deviceId?: string): void {
		this.changeCameraSubject.next(deviceId);
	}

	get allCameras$(): Observable<MediaDeviceInfo[]> {
		return this.allCamerasSubject.asObservable();
	}

	/**
	 * Obtiene el valor actual (sincronizadamente) del array de cámaras disponibles
	 * Útil cuando se necesita el valor inmediato sin esperar a observables
	 */
	get allCamerasValue(): MediaDeviceInfo[] {
		return this.allCamerasSubject.value;
	}

	/**
	 * Establece el flag para indicar que está en progreso una operación OCR
	 * Previene cambios automáticos de cámara durante el procesamiento
	 */
	public setProcessingOCR(isProcessing: boolean): void {
		// Limpiar timeout anterior si existe
		if (this.ocrProcessingTimeout) {
			clearTimeout(this.ocrProcessingTimeout);
			this.ocrProcessingTimeout = null;
		}

		this.isProcessingOCR = isProcessing;
		if (isProcessing) {
			// Timeout de seguridad: si no se limpia el flag en 10 segundos, limpiar automáticamente
			this.ocrProcessingTimeout = setTimeout(() => {
				this.isProcessingOCR = false;
				this.ocrProcessingTimeout = null;
			}, 10000);
		}
	}

	/** Detiene el stream activo sin solicitar uno nuevo (p. ej. permisos denegados). */
	public releaseActiveStream(): void {
		const active = this.streamSubject.value;
		if (active) {
			active.getTracks().forEach((track: MediaStreamTrack) => track.stop());
		}
		this.stream = null;
	}

	/**
	 * Stream de cámara no utilizable: sin stream, track ended o MediaStream inactivo.
	 */
	public isCameraStreamUnusable(stream: MediaStream | null = this.streamSubject.value): boolean {
		if (!stream) return true;
		if (!stream.active) return true;
		const track = stream.getVideoTracks()[0];
		return !track || track.readyState === 'ended';
	}

	/**
	 * Stream heredado que debe descartarse: existe pero no es utilizable.
	 * `null`/`undefined` no es stale (hará falta `getUserMedia` o aún no hay handoff).
	 */
	public isStaleInheritedStream(stream: MediaStream | null | undefined): boolean {
		return stream != null && this.isCameraStreamUnusable(stream);
	}

	/**
	 * Libera el stream si no es utilizable (sin emitir `changeCamera`).
	 */
	public clearCameraStreamIfUnusable(): boolean {
		if (!this.isCameraStreamUnusable()) return false;
		this.releaseActiveStream();
		return true;
	}

	/**
	 * Libera el stream al cambiar de operación sin emitir `changeCamera`.
	 * El siguiente `VideoComponent` hará un único `getUserMedia` en `startCamera`.
	 */
	public releaseStreamForOperationHandoff(): void {
		if (!this.streamSubject.value) return;
		this.releaseActiveStream();
	}

	/**
	 * Reinicia el stream de la cámara limpiando el stream actual y emitiendo un evento de cambio de cámara.
	 * Usar solo cuando el `VideoComponent` actual sigue montado (p. ej. reintento dentro de la misma operación).
	 */
	public resetCameraStream(): void {
		// Parar todos los tracks del stream actual
		if (this.streamSubject.value) {
			this.streamSubject.value.getTracks().forEach((track) => {
				track.stop();
			});
		}
		// Establecer stream a null para forzar que el video component obtenga uno nuevo
		this.stream = null;
		// Emitir cambio de cámara para que el video component reinicie el stream
		this.changeCamera();
	}

	/**
	 * Establece la configuración del proceso para obtener el flag de required_recording
	 */
	public updateRecordingConfig(required_recording: boolean): void {
		this.hasAudio = required_recording || false;
	}

	/**
	 * Determina si se debe incluir audio en las constraints
	 * @returns true si debe incluir audio, false en caso contrario
	 */
	public shouldIncludeAudio(): boolean {
		return this.hasAudio;
	}
}
